import * as THREE from 'three'

import GlassSegment from '@/model/glassSegment'
import MathsUtils from '@/utils/mathsUtils'
import Types from '../types'


/**
 * General utility functions for the Glass Editor App
 */
export default class EditorUtils {


  /**
   * Cleanup an array of GlassSegments for use with storing objects as a library item
   * Moves the segments to be around an origin point, and cleans up edgework & other details
   * 
   * Mutates the segments in place in the segment list
   */
  static cleanupLibraryItemGlassSegmentList(segmentList, moveOffset) {
    segmentList.forEach(segment => {
      // Cleanup the segment for the library
      segment.isEdgeworkAvailable = false
      segment.orderEditorEdgeworkConfigId = null
      segment.edgeworkSettingValue = 0

      // Reset positions to be offset from origin as required
      segment.startPosition.set(segment.startPosition.x + moveOffset.x, segment.startPosition.y + moveOffset.y)
      segment.endPosition.set(segment.endPosition.x + moveOffset.x, segment.endPosition.y + moveOffset.y)
      segment.arcCenterPosition.set(segment.arcCenterPosition.x + moveOffset.x, segment.arcCenterPosition.y + moveOffset.y)
    })
  }


  /**
   * Generate a thumbnail for a cutout based on a list of shapes each containing segments
   * Returns a string containing a DataURI based image for the library items to display
   */
  static generateCutoutThumbnailDataURI(shapeSegmentList, thumbnailWidth, thumbnailHeight) {
    let lineColour = '#EFEFEF'
    let lineWidth = 2.5
    let thumbPadding = 6
    let pixelAdjust = 0.5     // Sub-pixel adjustment to stop antialiasing creating blurry lines

    if(shapeSegmentList.length == 0)
      return ''

    var completeBounds = new THREE.Box2()
    shapeSegmentList.forEach(segmentList => {
      let shapeBounds = MathsUtils.calculateGlassSegmentListBounds(segmentList)
      completeBounds.union(shapeBounds)
    })

    let boundWidth = (completeBounds.max.x - completeBounds.min.x)
    let boundHeight = (completeBounds.max.y - completeBounds.min.y)
    let centreX = (completeBounds.min.x + completeBounds.max.x) / 2
    let centreY = (completeBounds.min.y + completeBounds.max.y) / 2

    let scaleX = boundWidth > 0 ? (thumbnailWidth - thumbPadding*2) / boundWidth : 1
    let scaleY = boundHeight > 0 ? (thumbnailHeight - thumbPadding*2) / boundHeight : 1

    var scale = Math.min(scaleX, scaleY)

    // Create a canvas element & draw the point list into that
    let canvasElement = document.createElement('canvas')
    canvasElement.width = thumbnailWidth
    canvasElement.height = thumbnailHeight
    
    var ctx = canvasElement.getContext("2d")
    ctx.clearRect(0, 0, thumbnailWidth, thumbnailHeight)

    ctx.strokeStyle = lineColour
    ctx.lineWidth = lineWidth
    ctx.lineJoin = 'round'
    ctx.lineCap = 'round'

    let transformPointX = (coordinate) => {
      return Math.round(thumbnailWidth/2 + (coordinate - centreX)*scale) + pixelAdjust
    }
    let transformPointY = (coordinate) => {
      return Math.round(thumbnailHeight/2 + (coordinate - centreY)*scale) + pixelAdjust
    }


    shapeSegmentList.forEach(segmentList => {
      ctx.beginPath()

      segmentList.forEach((segment, index) => {
        if(index == 0) {
          if(segment.type == Types.SegmentTypes.line) {
            if(segment.startCornerRadius == 0.0) {
              ctx.moveTo(transformPointX(segment.startPosition.x), transformPointY(segment.startPosition.y))
            } else {
              // Adjust for the corner radius
              let roundCornerData = MathsUtils.calculateSegmentDeltaCornerRounding(segment, true, false)

              if(!roundCornerData) {
                ctx.moveTo(transformPointX(segment.startPosition.x), transformPointY(segment.startPosition.y))
              } else {
                ctx.ellipse(transformPointX(roundCornerData.center.x),
                            transformPointY(roundCornerData.center.y),
                            roundCornerData.radius * scale,
                            roundCornerData.radius * scale,
                            0,
                            roundCornerData.startAngle,
                            roundCornerData.endAngle,
                            roundCornerData.clockwise)
              }
            }
          }
        }

        if(segment.type == Types.SegmentTypes.line) {
          if(segment.endCornerRadius == 0.0)
            ctx.lineTo(transformPointX(segment.endPosition.x), transformPointY(segment.endPosition.y))
          else {
            // Adjust for the corner radius
            let roundCornerData = MathsUtils.calculateSegmentDeltaCornerRounding(segment, false, false)

            if(!roundCornerData) {
              ctx.lineTo(transformPointX(segment.endPosition.x), transformPointY(segment.endPosition.y))
            } else {
              ctx.lineTo(transformPointX(roundCornerData.segment1End.x), transformPointY(roundCornerData.segment1End.y))
              ctx.ellipse(transformPointX(roundCornerData.center.x),
                          transformPointY(roundCornerData.center.y),
                          roundCornerData.radius * scale,
                          roundCornerData.radius * scale,
                          0,
                          roundCornerData.startAngle,
                          roundCornerData.endAngle,
                          roundCornerData.clockwise)
            }
          }

        } else if(segment.type == Types.SegmentTypes.arc) {
          ctx.ellipse(transformPointX(segment.arcCenterPosition.x),
                      transformPointY(segment.arcCenterPosition.y),
                      segment.arcRadius * scale,
                      segment.arcRadius * scale,
                      0,
                      MathsUtils.degreesToRadians(segment.arcStartAngle),
                      MathsUtils.degreesToRadians(segment.arcEndAngle),
                      !segment.arcClockwise)
        }
      })

      ctx.stroke()
    })

    // Convert the canvas element to an image/png DataURI string
    let imgData = canvasElement.toDataURL('image/png')

    return imgData
  }


  /**
   * Generate the segments for an oval shape given a center position, width & height
   */
  static generateSegmentsForOval(center, width, height) {
    // Approximating an Ellipse by Circular Arcs
    // Based on https://www.geometrictools.com/Documentation/ApproximateEllipse.pdf

    let segmentList = []
    var xRadius = Math.max(width / 2, 1)
    var yRadius = Math.max(height / 2, 1)

    let numArcs = 8   // Modify to adjust subdivision rate
    let arcList = []

    let a = xRadius
    let b = yRadius
    let a2 = a * a
    let b2 = b * b
    let ab = a * b

    let invB2mA2 = 1 / (b2 - a2)
    let curv0 = a / b2
    let curv1 = b / a2
    let invNumArcs = 1 / numArcs

    let arcPntList = []
    arcPntList.push(new THREE.Vector2(a, 0))
    for(let i=1; i<numArcs; i++) {
      let weight1 = i * invNumArcs
      let weight0 = 1 - weight1
      let curv = weight0 * curv0 + weight1 * curv1

      let tmp = Math.pow(ab / curv, 2 / 3)
      arcPntList.push(new THREE.Vector2(a * Math.sqrt(Math.abs((tmp - a2) * invB2mA2)), b * Math.sqrt(Math.abs((tmp - b2) * invB2mA2))))
    }
    arcPntList.push(new THREE.Vector2(0, b))

    // Convert 3 points along the ellipse into an arc
    // Uses start/end point plus a 3rd reference point to calculate the circle details
    let addArc = (refPoint, p1, p2, scalar) => {
      let refScaled = refPoint.clone().multiply(scalar)
      let scaledStart = p1.clone().multiply(scalar)
      let scaledEnd = p2.clone().multiply(scalar)

      let arcInfo = MathsUtils.calculateCircleInfoForPoints(refScaled, scaledStart, scaledEnd)
      if(arcInfo) {
        let arcStartAngle = MathsUtils.calculateArcPointAngle(arcInfo.position, scaledStart)
        let arcEndAngle = MathsUtils.calculateArcPointAngle(arcInfo.position, scaledEnd)
        
        let arcDetails = {
          arcStartAngle: arcStartAngle,
          arcEndAngle: arcEndAngle,
          arcCenterPosition: arcInfo.position,
          arcRadius: arcInfo.radius,
          arcClockwise: true
        }
        arcList.push(arcDetails)        
      }
    }

    // Convert points into 4 mirrored quadrants of the ellipse

    // Bottom Right
    let refP1 = new THREE.Vector2(arcPntList[1].x, -arcPntList[1].y)
    addArc(refP1, arcPntList[0], arcPntList[1], new THREE.Vector2(1,1))
    for(let iM=0, i=1, iP=2; i<arcPntList.length-2; ++iM, ++i, ++iP)
      addArc(arcPntList[iM], arcPntList[i], arcPntList[iP], new THREE.Vector2(1,1))
    let refPNm1 = new THREE.Vector2(-arcPntList[arcPntList.length-2].x, arcPntList[arcPntList.length-2].y)
    addArc(refPNm1, arcPntList[arcPntList.length-2], arcPntList[arcPntList.length-1], new THREE.Vector2(1,1))

    // Bottom Left
    refP1 = new THREE.Vector2(-arcPntList[arcPntList.length-2].x, arcPntList[arcPntList.length-2].y)
    addArc(refP1, arcPntList[arcPntList.length-1], arcPntList[arcPntList.length-2], new THREE.Vector2(-1,1))
    for(let iM=0, i=1, iP=2; i<arcPntList.length-2; ++iM, ++i, ++iP)
      addArc(arcPntList[arcPntList.length-iM-1], arcPntList[arcPntList.length-i-1], arcPntList[arcPntList.length-iP-1], new THREE.Vector2(-1,1))
    refPNm1 = new THREE.Vector2(arcPntList[1].x, -arcPntList[1].y)
    addArc(refPNm1, arcPntList[1], arcPntList[0], new THREE.Vector2(-1,1))

    // Top Left
    refP1 = new THREE.Vector2(arcPntList[1].x, -arcPntList[1].y)
    addArc(refP1, arcPntList[0], arcPntList[1], new THREE.Vector2(-1,-1))
    for(let iM=0, i=1, iP=2; i<arcPntList.length-2; ++iM, ++i, ++iP)
      addArc(arcPntList[iM], arcPntList[i], arcPntList[iP], new THREE.Vector2(-1,-1))
    refPNm1 = new THREE.Vector2(-arcPntList[arcPntList.length-2].x, arcPntList[arcPntList.length-2].y)
    addArc(refPNm1, arcPntList[arcPntList.length-2], arcPntList[arcPntList.length-1], new THREE.Vector2(-1,-1))

    // Top Right
    refP1 = new THREE.Vector2(-arcPntList[arcPntList.length-2].x, arcPntList[arcPntList.length-2].y)
    addArc(refP1, arcPntList[arcPntList.length-1], arcPntList[arcPntList.length-2], new THREE.Vector2(1,-1))
    for(let iM=0, i=1, iP=2; i<arcPntList.length-2; ++iM, ++i, ++iP)
      addArc(arcPntList[arcPntList.length-iM-1], arcPntList[arcPntList.length-i-1], arcPntList[arcPntList.length-iP-1], new THREE.Vector2(1,-1))
    refPNm1 = new THREE.Vector2(arcPntList[1].x, -arcPntList[1].y)
    addArc(refPNm1, arcPntList[1], arcPntList[0], new THREE.Vector2(1,-1))


    // Convert to segments
    arcList.forEach(arcInfo => {
      let arcSeg = new GlassSegment(Types.SegmentTypes.arc, {
        arcCenterPosition: arcInfo.arcCenterPosition.clone().add(center),
        arcRadius: arcInfo.arcRadius,
        arcStartAngle: arcInfo.arcStartAngle,
        arcEndAngle: arcInfo.arcEndAngle,
        arcClockwise: true
      })
      segmentList.push(arcSeg)
    })

    return segmentList
  }
}
